/**
 * Original line icon set (24×24, 1.7 stroke, round caps). Decorative by default; give the
 * surrounding control an accessible name.
 */
import type { ReactElement, SVGProps } from 'react';

export type IconName =
  | 'play'
  | 'pause'
  | 'next'
  | 'previous'
  | 'chevronUp'
  | 'chevronDown'
  | 'gear'
  | 'camera'
  | 'expand'
  | 'collapse'
  | 'sun'
  | 'cloud'
  | 'rain'
  | 'auto'
  | 'clock'
  | 'moon'
  | 'shuffle'
  | 'repeat'
  | 'repeatOne'
  | 'volume'
  | 'volumeOff'
  | 'close'
  | 'back'
  | 'arrowRight'
  | 'leaf'
  | 'queue'
  | 'timer'
  | 'info'
  | 'check'
  | 'plus'
  | 'minus'
  | 'search';

const P: Record<IconName, ReactElement> = {
  play: <path d="M8 5.5v13l10-6.5z" fill="currentColor" stroke="none" />,
  pause: (
    <g fill="currentColor" stroke="none">
      <rect x="6.5" y="5.5" width="3.8" height="13" rx="1.1" />
      <rect x="13.7" y="5.5" width="3.8" height="13" rx="1.1" />
    </g>
  ),
  next: (
    <g>
      <path d="M6 6.5v11l8-5.5z" fill="currentColor" stroke="none" />
      <path d="M17.5 6.5v11" />
    </g>
  ),
  previous: (
    <g>
      <path d="M18 6.5v11l-8-5.5z" fill="currentColor" stroke="none" />
      <path d="M6.5 6.5v11" />
    </g>
  ),
  chevronUp: <path d="M6.5 14.5l5.5-5.5 5.5 5.5" />,
  chevronDown: <path d="M6.5 9.5l5.5 5.5 5.5-5.5" />,
  gear: (
    <g>
      <path d="M18.57 10.69 L20.67 11.25 L20.67 12.75 L18.57 13.31 L17.57 15.72 L18.66 17.60 L17.60 18.66 L15.72 17.57 L13.31 18.57 L12.75 20.67 L11.25 20.67 L10.69 18.57 L8.28 17.57 L6.40 18.66 L5.34 17.60 L6.43 15.72 L5.43 13.31 L3.33 12.75 L3.33 11.25 L5.43 10.69 L6.43 8.28 L5.34 6.40 L6.40 5.34 L8.28 6.43 L10.69 5.43 L11.25 3.33 L12.75 3.33 L13.31 5.43 L15.72 6.43 L17.60 5.34 L18.66 6.40 L17.57 8.28 Z" />
      <circle cx="12" cy="12" r="2.6" />
    </g>
  ),
  camera: (
    <g>
      <path d="M4.5 8.5h3l1.6-2.2h5.8l1.6 2.2h3a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13.2" r="3.2" />
    </g>
  ),
  expand: <path d="M4.5 9V4.5H9M15 4.5h4.5V9M19.5 15v4.5H15M9 19.5H4.5V15" />,
  collapse: <path d="M9 4.5V9H4.5M19.5 9H15V4.5M15 19.5V15h4.5M4.5 15H9v4.5" />,
  sun: (
    <g>
      <circle cx="12" cy="12" r="3.8" />
      <path d="M12 3v1.8M12 19.2V21M21 12h-1.8M4.8 12H3M18.4 5.6l-1.3 1.3M6.9 17.1l-1.3 1.3M18.4 18.4l-1.3-1.3M6.9 6.9L5.6 5.6" />
    </g>
  ),
  cloud: <path d="M7.5 18.5h9.2a3.8 3.8 0 0 0 .4-7.6 5.2 5.2 0 0 0-10-1.1A4.4 4.4 0 0 0 7.5 18.5z" />,
  rain: (
    <g>
      <path d="M7.5 14.5h9.2a3.6 3.6 0 0 0 .4-7.2 5 5 0 0 0-9.6-1A4.1 4.1 0 0 0 7.5 14.5z" />
      <path d="M8.5 17.5l-1 2.2M12.5 17.5l-1 2.2M16.5 17.5l-1 2.2" />
    </g>
  ),
  auto: (
    <g>
      <path d="M12 4.5c.6 3.6 2.9 5.9 6.5 6.5-3.6.6-5.9 2.9-6.5 6.5-.6-3.6-2.9-5.9-6.5-6.5 3.6-.6 5.9-2.9 6.5-6.5z" />
      <path d="M18.5 16.5c.2 1.2 1 2 2.2 2.2-1.2.2-2 1-2.2 2.2-.2-1.2-1-2-2.2-2.2 1.2-.2 2-1 2.2-2.2z" />
    </g>
  ),
  clock: (
    <g>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.5V12l3 2" />
    </g>
  ),
  moon: <path d="M18.5 14.5A7 7 0 0 1 9.5 5.5a7 7 0 1 0 9 9z" />,
  shuffle: <path d="M4 7.5h3.2c4.6 0 5 9 9.6 9H20M4 16.5h3.2c1.4 0 2.4-.8 3.2-2M20 7.5h-3.2c-1.4 0-2.4.8-3.2 2M17.5 5l2.5 2.5-2.5 2.5M17.5 14l2.5 2.5-2.5 2.5" />,
  repeat: <path d="M5 11V9.5a2 2 0 0 1 2-2h11.5M16 5l2.5 2.5L16 10M19 13v1.5a2 2 0 0 1-2 2H5.5M8 19l-2.5-2.5L8 14" />,
  repeatOne: (
    <g>
      <path d="M5 11V9.5a2 2 0 0 1 2-2h11.5M16 5l2.5 2.5L16 10M19 13v1.5a2 2 0 0 1-2 2H5.5M8 19l-2.5-2.5L8 14" />
      <path d="M11.3 10.6l1.2-.9v4.6" strokeWidth="1.5" />
    </g>
  ),
  volume: (
    <g>
      <path d="M4.5 9.5h3l4-3.5v12l-4-3.5h-3z" />
      <path d="M15 9a4 4 0 0 1 0 6M17.5 6.5a7.5 7.5 0 0 1 0 11" />
    </g>
  ),
  volumeOff: (
    <g>
      <path d="M4.5 9.5h3l4-3.5v12l-4-3.5h-3z" />
      <path d="M15.5 9.5l5 5M20.5 9.5l-5 5" />
    </g>
  ),
  close: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />,
  back: <path d="M19 12H5.5M11 6l-6 6 6 6" />,
  arrowRight: <path d="M5 12h13.5M13 6l6 6-6 6" />,
  leaf: (
    <g>
      <path d="M5 19c0-8 5-13.5 14-14 .2 9-5.5 14-14 14z" />
      <path d="M5 19c3-3.5 6-6.5 9.5-9" />
    </g>
  ),
  queue: <path d="M4.5 7h11M4.5 12h11M4.5 17h7M17.5 14.5v5l3.5-2.5z" />,
  timer: (
    <g>
      <path d="M17.8 14.2A6.5 6.5 0 0 1 9.8 6.2a6.5 6.5 0 1 0 8 8z" />
      <path d="M15.5 4.5h3l-3 3.5h3" strokeWidth="1.4" />
    </g>
  ),
  info: (
    <g>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 11v5M12 8v.2" />
    </g>
  ),
  check: <path d="M5.5 12.5l4 4 9-9.5" />,
  plus: <path d="M12 6v12M6 12h12" />,
  minus: <path d="M6 12h12" />,
  search: (
    <g>
      <circle cx="11" cy="11" r="6" />
      <path d="M15.5 15.5L20 20" />
    </g>
  ),
};

export function Icon({ name, size = 20, ...rest }: { name: IconName; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {P[name]}
    </svg>
  );
}
